import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import {Check, ErrorOutline} from '@mui/icons-material';
import React, {PropsWithChildren, useEffect, useMemo} from 'react';
import {useReviewContext} from '../../contexts/ReviewContext';
import {usePrevious} from '../../hooks/usePrevious';
import {themeColors} from '../../theme/Theme';
import {useHandoverNoteStyles} from '../../features/notes/handoverNoteStyles';
import {FormikNote, HandoverNoteFields} from './HandoverNotesHelpers';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {SaltusAny} from '../../utils/SaltusAny';
import {UPDATES} from '../../shared/UPDATES';

export const NotesMenuItem: React.FC<
  PropsWithChildren<{
    menuItemScreen: HandoverNoteFields;
    currentScreen: HandoverNoteFields;
    setPage: React.Dispatch<React.SetStateAction<HandoverNoteFields>>;
    onUpdateNote: (noteType: HandoverNoteFields) => SaltusAny;
    readOnly: boolean;
  }>
> = ({menuItemScreen, currentScreen, setPage, children, onUpdateNote, readOnly}) => {
  const {reviewDetails} = useReviewContext();
  const classes = useHandoverNoteStyles();
  const theme = useTheme();
  const [field, meta, helpers] = useAutoField<FormikNote>(menuItemScreen);
  const [_, reviewedMeta] = useAutoField<FormikNote>(`${menuItemScreen}.reviewed`);
  const previouslyReviewed = usePrevious(field.value.reviewed);
  const active = useMemo(() => {
    return currentScreen === menuItemScreen;
  }, [currentScreen]);
  useEffect(() => {
    if (
      typeof previouslyReviewed === 'boolean' &&
      previouslyReviewed !== field.value.reviewed
    ) {
      // yes this runs a lot, but on page load, we need to check whether the review's note types are correct:
      // aka a review may change from holistic to focused (eg pensions)
      onUpdateNote(field.value.displayName);
    }
  }, [field.value.reviewed, previouslyReviewed]);
  const isDone =
    meta.value.reviewed || meta.value.body?.length > 0 || meta.value.markupBody?.length;
  const isError =
    (readOnly || reviewedMeta.touched || reviewDetails?.notesEdited) &&
    meta.value.required &&
    !meta.value.reviewed &&
    !meta.value.body &&
    !meta.value.markupBody;
  return (
    <ListItem
      data-test={menuItemScreen}
      sx={{
        ...classes.rowItem,
        ...(isError ? {backgroundColor: themeColors.lightAlert} : {}),
        ...(isDone ? {backgroundColor: themeColors.lightGreen} : {}),
        ...(active ? classes.activeItem : {}),
      }}
      onClick={() => {
        setPage(menuItemScreen);
      }}
    >
      <Box sx={{width: 30, pr: 1, display: 'flex', justifyContent: 'center'}}>
        {isDone ? <Check data-test="Status Done" htmlColor={themeColors.green} /> : null}
        {isError ? <ErrorOutline color="error" data-test="Status Error" /> : null}
      </Box>
      <Box display="flex" justifyContent="space-between" flex={1}>
        <ListItemText
          primaryTypographyProps={{
            style: {
              paddingBottom: 0,
              fontSize: 16,
              userSelect: 'none',
              display: 'flex',
            },
          }}
          sx={classes.itemText}
        >
          {children}
        </ListItemText>

        <FormControl>
          <FormControlLabel
            disabled={readOnly}
            onClick={(e) => {
              if (readOnly) {
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              helpers.setValue(UPDATES._4102, {
                ...field.value,
                reviewed: !field.value.reviewed,
              });
            }}
            control={
              <Checkbox
                data-test="No changes"
                size="small"
                disableRipple={readOnly}
                sx={{
                  '& svg': {
                    color: active ? themeColors.white : themeColors.backgroundDark,
                  },
                  lineHeight: 1,
                }}
                checked={field.value.reviewed}
              />
            }
            label="No changes"
          />
        </FormControl>
      </Box>
    </ListItem>
  );
};
